// src/contexts/ProductContext.tsx
import React, { createContext, useContext, useState, ReactNode } from "react";
import { Product } from "@visitproof/types";
import { ProductService } from "../services/ProductService";
import { useAccount } from "./AccountContext";

interface ProductContextProps {
  products: Product[];
  selectedProduct: Product | null;
  deleteProductById: (productId: string) => void;
  setSelectedProduct: (productId: string) => void;
  reloadProducts: () => void;
  loading: boolean;
  error: string | null;
}

const ProductContext = createContext<ProductContextProps | undefined>(
  undefined,
);

export const useProduct = (): ProductContextProps => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("useProduct must be used within a ProductProvider");
  }
  return context;
};

export const ProductProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { account } = useAccount();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const reloadProducts = async () => {
    if (!account?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const productList = await ProductService.getAllProducts(account.id);
      setProducts(productList);
    } catch (err) {
      setError("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const setSelectedProductById = async (productId: string) => {
    setLoading(true);
    setError(null);
    try {
      const productData = await ProductService.getProduct(productId);
      setSelectedProduct(productData);
    } catch (err) {
      setError(`Failed to load product with ID: ${productId}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteProductById = async (productId: string) => {
    await ProductService.deleteProduct(productId);
    setProducts(products.filter((product) => product.id !== productId));
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        selectedProduct,
        setSelectedProduct: setSelectedProductById,
        reloadProducts,
        loading,
        error,
        deleteProductById,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};
